import {
  type ChatMessage,
  ChatMessageRole,
  type VisionAiStoreMessage,
} from '@/components/VisionAi/chat-panel-components/utils/VisionChatSidePanel.types';
import { INTENT_TYPES } from '@/components/VisionAi/constants';
import { getMessageTextForIntentType } from '@/components/VisionAi/utils/message';
import { parseUnifiedWidgets } from '@/components/VisionAi/utils/parseUnifiedWidgets';
import { VISION_AI_SENDER } from '@/config';

/**
 * The API returns `errorCode` as a number, string, null, or undefined.
 * getMessageTextForIntentType expects number | null, so we coerce here.
 *
 * Coerces an error code to `number | null` for {@link getMessageTextForIntentType}.
 *
 * @param errorCode - Raw error code from the API response.
 * @returns The numeric error code, or `null` if absent / unparseable.
 */
function normalizeErrorCodeForDisplay(
  errorCode: number | string | null | undefined,
): number | null {
  if (errorCode == null || errorCode === '') return null;
  const numericErrorCode = typeof errorCode === 'number' ? errorCode : Number(errorCode);
  return Number.isNaN(numericErrorCode) ? null : numericErrorCode;
}

/**
 * Converts a {@link VisionAiStoreMessage} (store shape) into a {@link ChatMessage} (UI shape).
 */
export function mapVisionAiMessageToChatMessage(msg: VisionAiStoreMessage): ChatMessage {
  const role =
    msg.sender === VISION_AI_SENDER.USER ? ChatMessageRole.User : ChatMessageRole.Assistant;
  const errorCode = normalizeErrorCodeForDisplay(msg.errorCode);

  const text = getMessageTextForIntentType(
    msg.message ?? '',
    msg.sender,
    msg.intentType ?? '',
    errorCode,
    msg.isWarningMessage,
  );

  const widgets =
    role === ChatMessageRole.Assistant && msg.intentType === INTENT_TYPES.UNIFIED
      ? parseUnifiedWidgets(msg.additionalContent)
      : undefined;

  return {
    id: msg.id,
    conversationId: msg.conversationId,
    role,
    text,
    timestamp: new Date(msg.createdAt),
    intentType: msg.intentType,
    intentQuery: msg.intentQuery,
    additionalContent: msg.additionalContent,
    feedback: msg.feedback,
    isWarningMessage: msg.isWarningMessage,
    errorCode: msg.errorCode != null ? String(msg.errorCode) : null,
    widgets,
  };
}

export function mapVisionAiMessagesToChatMessages(messages: VisionAiStoreMessage[]): ChatMessage[] {
  return messages.map(mapVisionAiMessageToChatMessage);
}
